import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { Exceptions, routes } from '@blockpulse3/data/shared';
import {
  useCreateUserMutation,
  useLoginMutation,
  useVerifyEmailMutation,
} from '@blockpulse3/graphql/hooks';
import { useErrorToast } from '@blockpulse3/ui/commons';

import { useUser } from '../../providers';
import { CheckEmail } from '../CheckEmail';
import { ISignUpForm, SignUpForm, signUpFormDefaults } from '../SignUpForm';

type Props = unknown;

/**
 * InvitationSignUpView.
 * SignUpForm with invite logic.
 *
 * @returns {JSX.Element}
 */
export function InvitationSignUpView(): JSX.Element {
  const i18nInvitationSignUp = useTranslations('InvitationSignUp');

  const { loadUser } = useUser();
  const [searchParams] = useSearchParams();

  const [login] = useLoginMutation();
  const [verifyEmail] = useVerifyEmailMutation();
  const [createUser, { loading }] = useCreateUserMutation();

  const [shouldVerifyEmail, setShouldVerifyEmail] = useState<boolean>(false);

  const redirectEmail = searchParams.get('email') || '';
  const redirectToken = searchParams.get('token') || '';
  const isEmailDisabled = !!redirectEmail;

  const errorToast = useErrorToast();

  const handleSubmit = (data: ISignUpForm): void => {
    createUser({
      variables: {
        createUserInput: {
          firstName: '',
          lastName: '',
          email: data.email,
          password: data.password,
          timezone: data.timezone,
        },
      },
      onCompleted: (res) => {
        const loginInput = {
          email: res.createUser.email,
          password: data.password,
          timezone: data.timezone,
        };
        verifyEmail({
          variables: { verifyEmailInput: { email: data.email, token: redirectToken } },
          onCompleted: () => {
            login({
              variables: { loginInput },
              onCompleted: (res) => {
                if (res.login.access_token) {
                  localStorage.setItem('token', res.login.access_token);
                  loadUser();
                }
              },
            });
          },
          onError: (err: unknown) => {
            login({
              variables: { loginInput },
              onCompleted: () => {
                setShouldVerifyEmail(true);
              },
            });
            errorToast({ title: i18nInvitationSignUp('Toast.Error') });
          },
        });
      },
      onError: (err: unknown) => {
        const error = err as Error;
        if (error.message === Exceptions.AlreadyUsedEmail) {
          errorToast({ title: i18nInvitationSignUp('Toast.AlreadyUsedEmail') });
        } else {
          errorToast({ title: i18nInvitationSignUp('Toast.Error') });
        }
      },
    });
  };

  if (shouldVerifyEmail) {
    return <CheckEmail email={redirectEmail} />;
  }

  if (!isEmailDisabled || !redirectToken) {
    /* ** Redirect to default signup if no parameters ** */
    return <Navigate to={routes.signup.href} />;
  }

  return (
    <SignUpForm
      defaultValues={{ ...signUpFormDefaults, email: redirectEmail }}
      isDisabled={isEmailDisabled}
      loading={loading}
      onSubmit={handleSubmit}
    />
  );
}

export type InvitationSignUpViewProps = Props;
